import { Injectable } from "@angular/core";
import { Router } from "@angular/router";
import { catchError, of, take } from "rxjs";
import { ApplePayService } from "./apple-pay.service";
import { ChequeSyncService } from "./cheque-sync.service";
import { DataService } from "./data.service";
import { GooglePayService } from "./google-pay.service";
import { OnlinePaymentService } from "./online-payment.service";
import { PaymentEventsService } from "./payment-events.service";
import { OrderService } from "./order.service";
import { TranslateService } from "@ngx-translate/core";
import { PaymentMethod } from "../models/cart.model";
import { NotificationPayload } from "../models/notification.model";
import { PayAtTableNiPayload, PayAtTableResponse } from "../models/pay-at-table.model";
import { ApiService } from "./api.service";
import { NotificationType, PaymentType } from "../shared/enums";
import { ToastService } from "./toast.service";

type LoadingSetter = (value: boolean) => void;

@Injectable({
  providedIn: "root",
})
export class PayAtTablePaymentService {
  // Marks the screen the guest opened PAT payment from, so the top bar's back button can
  // return there even after a gateway redirect round-trip (localStorage survives that;
  // history.state doesn't, since a returning gateway redirect is a fresh history entry).
  private readonly ORIGIN_KEY = "pat_payment_origin";

  constructor(
    private applePayService: ApplePayService,
    private googlePayService: GooglePayService,
    private onlinePaymentService: OnlinePaymentService,
    private paymentEventsService: PaymentEventsService,
    private orderService: OrderService,
    private dataService: DataService,
    private apiService: ApiService,
    private toastService: ToastService,
    private router: Router,
    private translateService: TranslateService,
    private chequeSyncService: ChequeSyncService
  ) {
    // A cancelled gateway never calls back on its own; release the PAT lock if we already left the page.
    this.paymentEventsService.paymentCancelled.subscribe(() =>
      this.chequeSyncService.abortPaymentFlow()
    );
  }

  public setOrigin(url: string): void {
    const tableId = this.dataService.getTableId();
    if (!tableId) return;
    localStorage.setItem(`${this.ORIGIN_KEY}_${tableId}`, url);
  }

  /** Read-once: returns the captured origin (if any) and clears it so it can't be reused stale. */
  public consumeOrigin(): string | null {
    const tableId = this.dataService.getTableId();
    if (!tableId) return null;
    const key = `${this.ORIGIN_KEY}_${tableId}`;
    const url = localStorage.getItem(key);
    localStorage.removeItem(key);
    return url;
  }

  /** Drops Apple Pay from the list when it's unavailable on this device/browser. */
  public filterOutUnavailableApplePay(methods: PaymentMethod[]): PaymentMethod[] {
    if (this.applePayService.isApplePayAvailable()) {
      return methods;
    }
    return methods.filter((method) => method.key !== "applepay");
  }

  /**
   * Resolves whether Google Pay is available and, if not, returns the list with it removed.
   * Returns null when no change is needed (Google Pay is available, or the check failed),
   * so callers can tell "nothing to update" apart from "here's the filtered list".
   */
  public async filterOutUnavailableGooglePay(methods: PaymentMethod[]): Promise<PaymentMethod[] | null> {
    try {
      const isReady = await this.googlePayService.isGooglePayAvailable();
      return isReady ? null : methods.filter((method) => method.key !== "googlepay");
    } catch {
      return null;
    }
  }

  public startPayment(
    payload: PayAtTableNiPayload,
    paymentType: PaymentMethod | undefined,
    amount: number,
    currency: string,
    setLoading?: LoadingSetter,
    voucherName?: string
  ): void {
    if (!paymentType) {
      return;
    }

    if (paymentType.key === "applepay") {
      this.submitApplePay(payload, amount, voucherName);
      return;
    }

    if (paymentType.key === "googlepay") {
      void this.submitGooglePay(payload, amount, currency, setLoading, voucherName);
      return;
    }

    if (paymentType.is_online) {
      setLoading?.(true);
      this.onlinePaymentService.startPayAtTablePayment(payload, paymentType.key);
      return;
    }

    this.sendNotification(paymentType.key as PaymentType);
  }

  public applyPostpaidCheckoutThenLoadOrders(
    voucherName?: string
  ) {
    const tableId = this.dataService.table()?.id;
    const restaurantId = this.dataService.restaurant().id;
    const userId = this.dataService.getUserId();

    if (!tableId || !restaurantId || !userId) {
      this.router.navigate(["/menu", { openMenu: true }]);
      return of<PayAtTableResponse | null>(null);
    }

    const payload = {
      user_id: userId,
      table_id: tableId,
      voucher_name: voucherName || undefined,
    };

    return this.apiService.getPayAtTableOrders(restaurantId, payload);
  }

  public confirmPostpaidCheckoutWithPayAtTablePayload(
    payAtTablePayload: PayAtTableNiPayload,
    voucherName?: string,
  ) {
    const tableId = this.dataService.table()?.id;
    const restaurantId = this.dataService.restaurant().id;
    const userId = this.dataService.getUserId();

    if (!tableId || !restaurantId || !userId) {
      this.router.navigate(["/menu", { openMenu: true }]);
      return of(null);
    }

    const payload = {
      user_id: userId,
      table_id: tableId,
      voucher_name: voucherName,
      payment_mode: payAtTablePayload.payment_mode,
      payment_details: payAtTablePayload.payment_details,
      tip_amount: payAtTablePayload.tip_amount
    };

    return this.apiService
      .confirmPostpaidCheckout(restaurantId, payload)
      .pipe(catchError(() => of(null)));
  }

  private submitApplePay(payload: PayAtTableNiPayload, amount: number, voucherName?: string): void {
    if (!this.applePayService.isApplePayAvailable()) {
      this.toastService.warning("Apple Pay is not available.");
      return;
    }

    if (amount <= 0) {
      this.toastService.warning("Nothing to charge.");
      return;
    }

    const session = this.applePayService.createApplePaySession(this.dataService.restaurant().currency ?? "AED", amount);

    this.applePayService.startPayAtTableApplePaySession(session, payload, voucherName);

    const sub = this.applePayService.ApplePaySuccess.pipe(take(1)).subscribe((ok) => {
      if (ok) {
        const txId = this.applePayService.getTransactionId();
        if (txId) {
          this.orderService.setLastTransaction(txId).subscribe({
            next: () => this.router.navigate(["/bill-paid"]),
          });
        }
      } else {
        this.toastService.error(this.translateService.instant("PAYMENT.ERROR"));
      }
      sub.unsubscribe();
    });
  }

  private async submitGooglePay(
    payload: PayAtTableNiPayload,
    amount: number,
    currency: string,
    setLoading?: LoadingSetter,
    voucherName?: string
  ): Promise<void> {
    if (amount <= 0) {
      this.toastService.warning("Nothing to charge.");
      return;
    }

    const totalStr = Number(amount ?? 0).toFixed(2);

    setLoading?.(true);

    this.googlePayService.GooglePaySuccess.pipe(take(1)).subscribe((ok) => {
      setLoading?.(false);
      if (ok) {
        const txId = this.googlePayService.getTransactionId() || undefined;
        if (txId) {
          this.orderService.setLastTransaction(txId).subscribe({
            next: () => this.router.navigate(["/bill-paid"]),
            error: () => this.toastService.error(this.translateService.instant('PAYMENT.ERROR')),
          });
        }
      } else {
        this.toastService.error(this.translateService.instant("PAYMENT.ERROR"));
      }
    });

    try {
      await this.googlePayService.startPayAtTableGooglePaySession(
        payload,
        totalStr,
        currency,
        voucherName
      );
    } catch {
      setLoading?.(false);
      this.toastService.error(this.translateService.instant("PAYMENT.ERROR"));
    }
  }

  private sendNotification(paymentType: PaymentType): void {
    const tableId = this.dataService.table()?.id;
    const userId = this.dataService.getUserId();
    if (!tableId || !userId) return;

    const payload: NotificationPayload = {
      type: NotificationType.BringTheBill,
      payment_type: paymentType,
      table: tableId,
      user: userId,
    };

    this.apiService.sendNotification(payload).subscribe(() => {
      this.toastService.success("Request sent.");
    });
  }
}
